/**
 * A clip URL, a preview, a spoken line and the voice that reads it - the editor
 * for one thing the phone says.
 *
 * Extracted from `NoticeCard` when the inbound tab needed the same fields for
 * its voicemail prompt. It is deliberately not in `fields.tsx`: that file holds
 * the primitives every panel is built from, and this is one specific shape made
 * of them.
 *
 * The clip wins when both are filled, which is said on the field rather than
 * left as a rule to discover: an operator who records a file and forgets to
 * clear the text should not have to wonder which one the customer got. The
 * synthesis row below turns that rule from a trap into the normal path - write
 * the sentence, pick a voice, and the address fills itself in.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type { NoticeClipInfo, NoticeLocale, NoticeSlot, NoticeVariant } from "../../../../lib/calls";
import { Field } from "../panel/fields";
import { Pill } from "../panel/fields";
import { NoticeClipRow } from "./NoticeClipRow";

export function NoticeVariantFields({
  slot,
  locale,
  value,
  onChange,
  savedClip,
  builtInAudio,
}: {
  /** Which announcement this is, so a generated recording is labelled with it. */
  slot: NoticeSlot;
  /** Which language tab it sits on. Absent is the base wording. */
  locale?: NoticeLocale;
  value: NoticeVariant;
  onChange: (next: NoticeVariant) => void;
  savedClip?: NoticeClipInfo;
  /** The shipped recording's address for this slot, from the server. */
  builtInAudio: string;
}) {
  const t = useT();
  return (
    <div className="grid gap-4">
      {/* THREE hints, because three different things can be true here and one
          sentence cannot describe them all.

          A base field for a slot that SHIPS a recording starts with the
          Estonian one already in it, and needs it, since Twilio has no Estonian
          voice. A language tab has no shipped recording at all and, in Russian
          or English, needs none. And a base field for a slot with nothing
          shipped - the two working-hours announcements and the caller menu's
          two questions - has an empty box that nothing will fill on its own,
          which the first hint would have described as already solved.

          Chosen from `builtInAudio` rather than from a list of slots, so a slot
          that gains or loses a recording does not need this file edited. */}
      <Field
        label={t("settings.notice.audioUrl")}
        hint={t(
          locale
            ? "settings.notice.audioUrlHintLocale"
            : builtInAudio
              ? "settings.notice.audioUrlHint"
              : "settings.notice.audioUrlHintNone",
        )}
      >
        <Pill
          value={value.audioUrl}
          onChange={(audioUrl) => onChange({ ...value, audioUrl })}
          placeholder="https://..."
          autoComplete="off"
          inputMode="url"
          mono
        />
      </Field>
      {/* Outside the Field on purpose: that one is a `label`, and a second
          interactive control inside a label sends its clicks to the input.

          Hearing it is the only check that counts, and the shipped path is not
          something anyone can judge by reading it. Same origin as this page, so
          a root-relative built-in address resolves as it stands; the route is
          public because Twilio has to fetch it without a session. Nothing is
          loaded until somebody presses play.

          `color-scheme` is what darkens the browser's own player skin. Nothing
          else in the app sets it, so without this the control stays a bright
          white pill on the dark card. */}
      {value.audioUrl.trim() ? (
        <audio
          className="-mt-1 w-full max-w-[420px] [color-scheme:light] dark:[color-scheme:dark]"
          controls
          preload="none"
          src={value.audioUrl.trim()}
        />
      ) : null}
      <Field
        label={t("settings.notice.text")}
        hint={t(
          locale
            ? "settings.notice.textHintLocale"
            : builtInAudio
              ? "settings.notice.textHint"
              : "settings.notice.textHintNone",
        )}
      >
        {/* `crm-area`, not a textarea inside `crm-inp`: that pill is a fixed
            flex row whose child rules only reach input and select. */}
        <textarea
          className="crm-area min-h-[86px]"
          value={value.text}
          rows={3}
          onChange={(e) => onChange({ ...value, text: e.target.value })}
        />
      </Field>
      {/* Under the text and not above it, because it reads the text: the order
          on screen is the order of the work - write the sentence, then have it
          spoken. */}
      <NoticeClipRow
        slot={slot}
        locale={locale}
        value={value}
        onChange={onChange}
        savedClip={savedClip}
        builtInAudio={builtInAudio}
      />
    </div>
  );
}
